import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { timeoutSignal } from '../utils/timeoutSignal';

/** What actually happened to the stream — reported to the caller for tracing. */
export type CameraStreamStatus =
  | { phase: 'ready'; transport: 'webrtc' | 'hls'; ms: number }
  | { phase: 'failed'; transport: 'webrtc' | 'hls' | 'none'; ms: number; reason: string };

interface CameraStreamProps {
  /** WebRTC WHEP endpoint URL (e.g. /cameraId/whep) */
  whepUrl?: string;
  /** HLS fallback URL (e.g. /cameraId/index.m3u8) */
  hlsUrl?: string;
  /** Max time to wait for WebRTC before falling back to HLS (ms) */
  webrtcTimeout?: number;
  /** Width of the container */
  width?: string;
  /** Height of the container */
  height?: string;
  /** Border radius */
  borderRadius?: string;
  /**
   * Called once the outcome is known, with how long it took. Lets the caller
   * put real camera timings on the call trace instead of guessing why a
   * resident saw a black screen.
   */
  onStatus?: (status: CameraStreamStatus) => void;
}

type StreamMode = 'connecting' | 'webrtc' | 'hls' | 'error';

/**
 * Resolve once the video element has decoded an actual frame.
 *
 * Completing the SDP exchange proves nothing: ICE can still fail, the sender
 * can stay silent, and the <video> stays black while the UI claims "LIVE".
 * `loadeddata` is the first point at which there is genuinely a picture.
 */
function waitForFirstFrame(video: HTMLVideoElement, timeoutMs: number): Promise<void> {
  if (video.readyState >= 2) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      video.removeEventListener('loadeddata', onData);
      video.removeEventListener('error', onError);
      clearTimeout(timer);
    };
    const onData = () => { cleanup(); resolve(); };
    const onError = () => { cleanup(); reject(new Error('video element error')); };
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error(`no frame within ${timeoutMs}ms`));
    }, timeoutMs);
    video.addEventListener('loadeddata', onData);
    video.addEventListener('error', onError);
  });
}

/**
 * Camera stream component with WebRTC (WHEP) primary and HLS fallback.
 * Used for doorbell cameras, entrance cameras, and videophone streams.
 *
 * Flow: Try WebRTC WHEP first → fall back to HLS after timeout → show error.
 */
export function CameraStream({
  whepUrl,
  hlsUrl,
  // 15s, niet 5s. Gemeten op 2026-09-07 aan de deurbel van 't Withof:
  //
  //     16:27:35  [path videophone-10999] stream is available and online
  //     16:27:43  [WebRTC] peer connection established, relay/udp
  //
  // Acht seconden tussen bron en verbinding. Met 5s gaf de client op om :40 -- drie
  // seconden voor het beeld er was -- en toonde 'Geen camera beschikbaar' terwijl mediamtx
  // even later gewoon twee tracks stond te leveren. Een koud `sourceOnDemand`-pad moet de
  // camera nog opbellen (mediamtx mag daar 10s over doen) en ICE loopt hier via een
  // TURN-relay, wat trager is dan de directe verbinding van vroeger.
  //
  // De regel: de client hoort NOOIT eerder op te geven dan de server. Zie ook de gelijke
  // correctie in de portal (LivePreview.jsx).
  webrtcTimeout = 15000,
  width = '100%',
  height = '250px',
  borderRadius = '12px',
  onStatus,
}: CameraStreamProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<StreamMode>('connecting');
  const videoRef = useRef<HTMLVideoElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const lastErrorRef = useRef<string>('');

  // Attempt WebRTC via WHEP
  const tryWebRTC = useCallback(async (): Promise<boolean> => {
    if (!whepUrl) return false;

    try {
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      });
      pcRef.current = pc;

      // Add transceiver for receiving video
      pc.addTransceiver('video', { direction: 'recvonly' });
      pc.addTransceiver('audio', { direction: 'recvonly' });

      // When track arrives, attach to video element
      pc.ontrack = (event) => {
        if (videoRef.current && event.streams[0]) {
          videoRef.current.srcObject = event.streams[0];
        }
      };

      // Create offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Wait for ICE gathering
      await new Promise<void>((resolve) => {
        if (pc.iceGatheringState === 'complete') {
          resolve();
        } else {
          pc.onicegatheringstatechange = () => {
            if (pc.iceGatheringState === 'complete') resolve();
          };
          // Don't wait forever for ICE
          setTimeout(resolve, 2000);
        }
      });

      // Send offer to WHEP endpoint
      const response = await fetch(whepUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/sdp' },
        body: pc.localDescription?.sdp,
        signal: timeoutSignal(webrtcTimeout),
      });

      if (!response.ok) {
        throw new Error(`WHEP failed: ${response.status}`);
      }

      const answerSdp = await response.text();
      await pc.setRemoteDescription(new RTCSessionDescription({
        type: 'answer',
        sdp: answerSdp,
      }));

      // The SDP exchange is done — but that is NOT a working stream. Wait for a
      // decoded frame, and bail out early if ICE gives up first. Previously the
      // function returned here, so a connection that never delivered video was
      // still reported as LIVE and the HLS fallback was skipped.
      const iceFailed = new Promise<never>((_, reject) => {
        pc.oniceconnectionstatechange = () => {
          if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'closed') {
            reject(new Error(`ICE ${pc.iceConnectionState}`));
          }
        };
      });
      const video = videoRef.current;
      if (!video) throw new Error('video element gone');
      await Promise.race([waitForFirstFrame(video, webrtcTimeout), iceFailed]);

      return true;
    } catch (err) {
      console.warn('[CameraStream] WebRTC failed, will try HLS:', err);
      lastErrorRef.current = err instanceof Error ? err.message : String(err);
      // Clean up failed peer connection
      if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
      }
      return false;
    }
  }, [whepUrl, webrtcTimeout]);

  // Attempt HLS playback (native or via direct src)
  const tryHLS = useCallback(async (): Promise<boolean> => {
    if (!hlsUrl || !videoRef.current) return false;

    const video = videoRef.current;
    try {
      // Native HLS (Safari/iOS) or best-effort direct src elsewhere. NOTE:
      // Android Chrome has no native HLS, so this path usually cannot recover a
      // failed WebRTC stream — it needs hls.js to be a real fallback.
      video.srcObject = null;
      video.src = hlsUrl;
      await video.play();
      // Same rule as WebRTC: playing is not the same as showing a picture.
      await waitForFirstFrame(video, webrtcTimeout);
      return true;
    } catch (err) {
      lastErrorRef.current = err instanceof Error ? err.message : String(err);
      return false;
    }
  }, [hlsUrl, webrtcTimeout]);

  // Connect with auto-fallback
  useEffect(() => {
    let cancelled = false;

    const startedAt = Date.now();
    const elapsed = () => Date.now() - startedAt;

    const connect = async () => {
      setMode('connecting');
      lastErrorRef.current = '';

      // Try WebRTC first
      if (whepUrl) {
        const webrtcOk = await tryWebRTC();
        if (cancelled) return;
        if (webrtcOk) {
          setMode('webrtc');
          onStatus?.({ phase: 'ready', transport: 'webrtc', ms: elapsed() });
          return;
        }
      }

      // Fall back to HLS
      const webrtcError = lastErrorRef.current;
      if (hlsUrl) {
        const hlsOk = await tryHLS();
        if (cancelled) return;
        if (hlsOk) {
          setMode('hls');
          onStatus?.({ phase: 'ready', transport: 'hls', ms: elapsed() });
          return;
        }
      }

      if (!cancelled) {
        setMode('error');
        onStatus?.({
          phase: 'failed',
          transport: whepUrl ? 'webrtc' : 'none',
          ms: elapsed(),
          reason: webrtcError || lastErrorRef.current || 'no stream url',
        });
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
      }
    };
  }, [whepUrl, hlsUrl, tryWebRTC, tryHLS, onStatus]);

  return (
    <div style={{
      position: 'relative',
      width,
      height,
      backgroundColor: '#000',
      borderRadius,
      overflow: 'hidden',
    }}>
      <video
        ref={videoRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
        autoPlay
        playsInline
        // MUST stay muted. Chrome allows muted autoplay unconditionally, but an
        // unmuted <video> needs a user gesture — which is why the entrance
        // camera stayed black on a freshly loaded (untouched) tablet, exactly
        // like the ringtone. Call audio comes over SIP, not over this WHEP
        // stream, so muting loses nothing and avoids doubling the doorstation
        // audio on top of the SIP leg.
        muted
      />
      {/* Connecting overlay */}
      {mode === 'connecting' && (
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: 'white',
        }}>
          <div style={{
            width: '36px',
            height: '36px',
            border: '3px solid rgba(255,255,255,0.2)',
            borderTop: '3px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
          <p style={{ marginTop: '12px', fontSize: 'var(--font-body)' }}>Connecting camera...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      {/* Error overlay */}
      {mode === 'error' && (
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: 'white',
        }}>
          <span style={{ fontSize: 'var(--font-heading)', marginBottom: '8px' }}>!</span>
          <p style={{ fontSize: 'var(--font-body)' }}>{t.doorbell.noCamera}</p>
        </div>
      )}
      {/* Mode badge */}
      {(mode === 'webrtc' || mode === 'hls') && (
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          padding: '2px 8px',
          backgroundColor: 'rgba(0,0,0,0.5)',
          borderRadius: '4px',
          fontSize: 'var(--font-small)',
          color: 'white',
          textTransform: 'uppercase',
        }}>
          {mode === 'webrtc' ? 'LIVE' : 'HLS'}
        </div>
      )}
    </div>
  );
}
